import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useAddRolesToUserMutation, keys as usersManagementKeys } from '~/queries/user-management'
import { useRolesListQuery } from '~/queries/user-roles'
import { reactSelectMenuPositionDef } from '~/utils/options'

const MODAL_ID = 'add-roles-to-user'

export interface FormValues {
  roles: IOption[]
}

const validationSchema = Yup.object({
  roles: Yup.array()
    .of(Yup.object().shape({ label: Yup.string().required(), value: Yup.string().required() }))
    .min(1, FORM_ERRORS.AT_LEAST_ONE)
    .required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  userId: string
  userRoles: string[]
}

const AddRolesForm = ({ onClose, onFailure, onSuccess, userId, userRoles }: FormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      roles: [],
    },
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: addRolesMutation } = useAddRolesToUserMutation()

  const { data: roles } = useRolesListQuery()

  const rolesOptions =
    roles?.data
      .filter(({ role_id }) => !userRoles.includes(role_id))
      .map(({ name, role_id }) => ({
        label: name,
        value: role_id,
      })) || []

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const roleIds = values.roles.map(({ value }) => value)
      await addRolesMutation({ roleIds, userId: userId })
      await queryClient.invalidateQueries({ queryKey: usersManagementKeys.details(userId) })
      onSuccess()
    } catch (e) {
      captureException(e)
      onFailure()
    }
  }

  const handleRoleChange = (value: IOption) => setValue('roles', [value])

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <Controller
          control={control}
          name="roles"
          render={({ field }) => (
            <SelectInput
              {...field}
              {...reactSelectMenuPositionDef(MODAL_ID)}
              autoFocus
              error={errors.roles?.message}
              label="Roles"
              onChange={(value) => handleRoleChange(value as IOption)}
              options={rolesOptions}
              placeholder="Select roles"
              required
            />
          )}
        />
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Save
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const AddRolesModal = ({ isOpen, onClose, onFailure, onSuccess, userId, userRoles }: ModalProps): ReactElement => {
  return (
    <Modal id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Add roles to user</ModalHeader>
      <AddRolesForm
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
        userId={userId}
        userRoles={userRoles}
      />
    </Modal>
  )
}

export default AddRolesModal
